import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DIALOGS_TABLE_NAME, type ConversationListItem } from '@/entities/conversations';
import type { TablePreset } from '@/entities/settings';
import { PresetActions, SelectionSaveOffer, SystemPresetCopyOffer, TablePresetsPopover } from '@/features/TablePresets';
import { useDialogsSection } from '@/pages/dialogs/model/useDialogsSection';
import { PRESETS_WIDTH_RESERVE_CLASS } from './ContentSearch/controlRowClasses';
import { DialogListDataGrid } from './DialogListDataGrid';
import { useDialogListState } from './model/useDialogListState';
import { usePresetSelection } from './model/usePresetSelection';

/** Пропсы секции списка диалогов */
export type DialogListSectionProps = {
  onLoaded?: (data: ConversationListItem[]) => void;
  /**
   * Узел вне колонки списка, в который переносится блок управления. Задают раскладки, где
   * рядом со списком стоит карточка диалога и колонке списка блоку тесно. Промежуток от
   * перенесённого блока до списка держит раскладка — по метке плотности на самом блоке.
   */
  controlsContainer?: HTMLElement | null;
  className?: string;
};

/**
 * Предупреждение подтверждения записи донастройки в пресет: кто ещё увидит изменения. Личный
 * пресет видит только его владелец — предупреждать не о ком.
 */
const getSaveChangesWarningKey = (preset: TablePreset) => {
  if (preset.isSystem) return 'saveChangesConfirmSystem';
  if (preset.isShared) return 'saveChangesConfirmShared';
  return null;
};

/**
 * Секция списка диалогов раздела «Диалоги» (и закреплённого пресета) на базе DataGrid.
 * Состояние списка ведёт `useDialogListState`; своё у раздела — слой отбора: пресеты таблицы
 * и выборка перехода с дашборда.
 */
export const DialogListSection: FC<DialogListSectionProps> = ({ className, controlsContainer, onLoaded }) => {
  // Раздел «Диалоги» и раздел каждого закреплённого пресета ведут свой вид списка: настройки,
  // пресет и слой выборки берутся из записи того раздела, в котором список открыт.
  const { sectionKey } = useDialogsSection(DIALOGS_TABLE_NAME);

  const { t } = useTranslation('tablePresets');
  const { gridProps, presets } = useDialogListState({ sectionKey, onLoaded });
  const { activePreset } = presets;
  const saveChangesWarningKey = activePreset && getSaveChangesWarningKey(activePreset);

  // Выбор пресета в списке: закреплённый ведёт в свой раздел, незакреплённый — отбор «Диалогов».
  const { onPresetSelect, canClearActive } = usePresetSelection();

  return (
    <DialogListDataGrid
      {...gridProps}
      className={className}
      controlsContainer={controlsContainer}
      canSaveLayer={presets.canSaveActivePreset}
      layerName={activePreset?.name}
      onSaveLayer={presets.onSaveActivePreset}
      // Запись меняет сохранённый вид пресета, у общего — и у других пользователей: сначала спросить
      saveLayerConfirmation={
        activePreset
          ? {
              title: t('saveChangesConfirmTitle', { name: activePreset.name }),
              description: saveChangesWarningKey ? t(saveChangesWarningKey) : undefined,
            }
          : undefined
      }
      canExitLayer={presets.canDeselectSelection}
      onExitLayer={presets.onDeselectSelection}
      layerBlock={
        <>
          <TablePresetsPopover
            sectionKey={sectionKey}
            widthReserveClassName={PRESETS_WIDTH_RESERVE_CLASS}
            onPresetSelect={onPresetSelect}
            selectionDescription={presets.selectionDescription}
          />
          {/* Управление применённым пресетом — рядом с его кнопкой, а не в строках списка */}
          <PresetActions sectionKey={sectionKey} onPresetSelect={onPresetSelect} canClearActive={canClearActive} />
        </>
      }
      // Предложения сохранить выборку и личную копию пресета — у строки управления, на виду, а не
      // у нижнего края экрана. Строка в потоке опускает таблицу вместе с панелью колонок, но грид
      // не пересоздаёт: панель остаётся открытой, отметки в ней — на месте.
      controlsFooterSlot={
        <>
          <SelectionSaveOffer
            sectionKey={sectionKey}
            selection={presets.unsavedSelection}
            selectionDescription={presets.selectionDescription}
            onDismiss={presets.onSelectionHintDismiss}
          />
          <SystemPresetCopyOffer sectionKey={sectionKey} selectionDescription={presets.selectionDescription} />
        </>
      }
    />
  );
};
